import { authRoles } from "app/main/consts";
import {
	convertLiveQueueRowToSearch,
	resolveLiveQueuePickupMoment,
	resolveLiveQueueDeliveryMoment,
	saveSearchesSessionStorage,
} from "app/main/searchV3/utils/searchUtils";

import Typography from "@material-ui/core/Typography";
import Chip from "@material-ui/core/Chip";
import Box from "@material-ui/core/Box";
import Tooltip from "@material-ui/core/Tooltip";

import {
	Schedule as ScheduleIcon,
	CheckCircle as CompletedIcon,
	Warning as WarningIcon,
	Info as InfoIcon,
	SwapHoriz as OnRelayIcon,
	Block as DeferredIcon,
} from "@material-ui/icons";

import moment from "moment";
import { openFormDialog } from "app/store/tools/formDialogSlice";
import { createRequest, createHeaders, throwResErrors } from "app/services/requestUtil";
import { showSnackbar } from "app/main/utils/snackbarUtil";

const searchEndpointLabel = (endpoint) => {
	if (!endpoint) return null;
	if (endpoint.city && endpoint.state) {
		return `${String(endpoint.city).toUpperCase()}, ${String(endpoint.state).toUpperCase()}`;
	}
	return endpoint.address || null;
};

const handleSearchForLoads = async (row, history) => {
	try {
		const searchFilters = convertLiveQueueRowToSearch(row);
		await saveSearchesSessionStorage(searchFilters);
		if (history) history.push("/search?id=EMPTY");
	} catch (error) {
		console.error("Error launching search from live queue:", error);
	}
};

const handleStartAgent = async (row, dispatch, snackbar) => {
	try {
		const carrierId = row.carrier || row.carrier_id;
		const truckId = row.truck?._id || row.truck_id;

		if (!carrierId || !truckId) {
			showSnackbar(snackbar, "Missing carrier or truck information", "error");
			return;
		}

		const request = createRequest();
		const config = { headers: createHeaders() };
		const url = `api/profile/carriers/${carrierId}/relay-schedules/chains/update-truck-plan`;

		const response = await request
			.post(url, { data: row }, config)
			.then((res) => res.data)
			.catch(throwResErrors);

		showSnackbar(snackbar, "Truck plan updated, opening agent configuration...", "success");

		const planId = response?.data?.planId;
		const originLabel = searchEndpointLabel(row.search_origin);
		const pickupMoment = resolveLiveQueuePickupMoment(row);
		const deliveryMoment = resolveLiveQueueDeliveryMoment(row);
		const availableDate = pickupMoment?.isValid() ? pickupMoment.toISOString() : null;
		const deliveryDate = deliveryMoment?.isValid() ? deliveryMoment.toISOString() : null;

		dispatch(
			openFormDialog({
				viewId: "AGENT_CONFIGURATION_VIEW",
				dataIds: {
					carrierId,
					truckId,
					planId,
					chainId: row.id,
					searchMode: "live_mode",
					truckInfo: {
						truckId: row.truck__view?.label || row.truck?.truckid || truckId,
						carrierName: row.carrier__view?.label || "Carrier",
						currentLocation: originLabel || "Unknown",
						availableDate,
						deliveryDate,
					},
				},
			})
		);
	} catch (error) {
		console.error("Error starting agent from live queue:", error);
		const errorMessage = error?.errors?.[0]?.message || error?.message || "Failed to update truck plan";
		showSnackbar(snackbar, errorMessage, "error");
	}
};

const formatDateTime = (dateTime) => {
	if (dateTime == null || dateTime === "") return "N/A";
	const m = moment.isMoment(dateTime) ? dateTime : moment(dateTime);
	if (!m.isValid()) return "N/A";
	return m.format("ddd MM/DD HH:mm");
};

const chainDayMoment = (item) => {
	if (!item?.date) return null;
	const m = moment(item.date, ["YYYY-MM-DD", "YYYY-MM-DD HH:mm", moment.ISO_8601], true);
	return m.isValid() ? m.clone().startOf("day") : null;
};

// Mirror backend `resolveRowDayTripWindow`: deliveries on row day, then pickups on row day after anchor.
// Delegates to the shared resolvers in searchUtils so the columns and the search/agent filters stay in sync.
const resolveAnchorDeliveryMoment = (item) => resolveLiveQueuePickupMoment(item);

const resolvePickupDateMoment = (item) => resolveLiveQueuePickupMoment(item);

const resolveDeliveryDateMoment = (item) => resolveLiveQueueDeliveryMoment(item);

// Active Trips: deliveries on the chain day, then pickups on that day after the anchor delivery.
const tripsActiveOnRowDay = (item) => {
	if (Array.isArray(item?.row_day_active_trips) && item.row_day_active_trips.length > 0) {
		return item.row_day_active_trips;
	}
	const day = chainDayMoment(item);
	if (!day) return item.covering_trips || [];
	const anchor = resolveAnchorDeliveryMoment(item);
	const deliveries = [];
	const pickupsAfter = [];
	(item.covering_trips || []).forEach((trip) => {
		const d = moment(trip.delivery_date || trip.delivery_window_date);
		if (d.isValid() && d.isSame(day, "day")) {
			deliveries.push({ trip, sortTs: d.valueOf() });
			return;
		}
		const p = moment(trip.pickup_date || trip.pickup_window_date);
		if (!p.isValid() || !p.isSame(day, "day")) return;
		if (anchor && !p.isAfter(anchor)) return;
		pickupsAfter.push({ trip, sortTs: p.valueOf() });
	});
	deliveries.sort((a, b) => a.sortTs - b.sortTs);
	pickupsAfter.sort((a, b) => a.sortTs - b.sortTs);
	return [...deliveries, ...pickupsAfter].map((e) => e.trip);
};

const PICKUP_DATE_SOURCE_LABELS = {
	ROW_DAY_TRIP_DELIVERY: "Trip delivery this day",
	SCHEDULE_START: "Schedule start",
	CURRENT_TRIP_DELIVERY: "Trip delivery this day",
	PREVIOUS_TRIP_DELIVERY: "Trip delivery this day",
	CHAIN_DATE: "Chain day",
	ROLLOVER_PREV_DAY: "Rolled from prior day",
};

const DELIVERY_DATE_SOURCE_LABELS = {
	PICKUP_AFTER_DELIVERY: "Next pickup after delivery",
	ROW_DAY_TRIP_PICKUP: "Next pickup after delivery",
	NEXT_DAY_CUTOFF: "Next day cutoff (20:00)",
	RELAY_COMMITMENT: "Next relay commitment",
	SCHEDULE_END: "Schedule end",
	NEXT_TRIP_PICKUP: "Next pickup after delivery",
	SCHEDULE_START: "Schedule end",
	CHAIN_DATE: "Chain day",
};

const renderSearchDate = (item, resolveMoment, sourceLabels, sourceCode) => {
	const m = resolveMoment(item);
	if (!m?.isValid()) return <Typography variant="body2">N/A</Typography>;
	const sourceText = sourceCode ? sourceLabels[sourceCode] || sourceCode : null;
	return (
		<Box>
			<Typography variant="body2">{formatDateTime(m)}</Typography>
			{sourceText && (
				<Typography variant="caption" style={{ color: "#757575", display: "block", lineHeight: 1.1 }}>
					{sourceText}
				</Typography>
			)}
		</Box>
	);
};

const exportSearchDate = (item, resolveMoment) => {
	const m = resolveMoment(item);
	return m?.isValid() ? formatDateTime(m) : "N/A";
};

// Bookable free time as `hours:minutes` (e.g. `26:45` = 26h 45m). Prefer the backend's computed
// `gap_hours` (the actual searchable window after the loading-deadline / weekend rules), so a
// COMPLETED day with no same-day room shows ~0 instead of the raw distance to the next trip — that
// real gap is recalculated on the rolled-forward row. Falls back to delivery − pickup if absent.
const formatTimeGap = (item) => {
	let totalMinutes;
	if (item != null && Number.isFinite(item.gap_hours)) {
		totalMinutes = Math.round(item.gap_hours * 60);
	} else {
		const pickup = resolvePickupDateMoment(item);
		const delivery = resolveDeliveryDateMoment(item);
		if (!pickup?.isValid() || !delivery?.isValid()) return "N/A";
		totalMinutes = delivery.diff(pickup, "minutes");
	}
	const sign = totalMinutes < 0 ? "-" : "";
	const abs = Math.abs(totalMinutes);
	const hours = Math.floor(abs / 60);
	const mins = abs % 60;
	return `${sign}${hours}:${String(mins).padStart(2, "0")}`;
};

const getStatusIcon = (status) => {
	switch (status) {
		case "COMPLETED":
		case "COVERED":
			return <CompletedIcon style={{ color: "#4caf50", fontSize: "1rem", marginRight: 4 }} />;
		case "ON_RELAY":
			return <OnRelayIcon style={{ color: "#7b1fa2", fontSize: "1rem", marginRight: 4 }} />;
		case "DEFERRED":
			return <DeferredIcon style={{ color: "#ef6c00", fontSize: "1rem", marginRight: 4 }} />;
		case "AVAILABLE":
			return <ScheduleIcon style={{ color: "#2196f3", fontSize: "1rem", marginRight: 4 }} />;
		case "URGENT":
			return <WarningIcon style={{ color: "#f44336", fontSize: "1rem", marginRight: 4 }} />;
		default:
			return <InfoIcon style={{ color: "#9e9e9e", fontSize: "1rem", marginRight: 4 }} />;
	}
};

const STATUS_LABELS = {
	AVAILABLE: "Available",
	ON_RELAY: "On Relay",
	DEFERRED: "Deferred",
	COMPLETED: "Completed",
};

// Map backend `source` codes (see yardAsSearchEndpoint / tripAsSearchEndpoint in relayScheduleManager.js)
// to user-friendly captions explaining where the origin/destination label came from.
const ENDPOINT_SOURCE_LABELS = {
	ROW_DAY_TRIP_DELIVERY: "After",
	PICKUP_AFTER_DELIVERY: "Next",
	NEXT_DAY_CUTOFF: "Next day cutoff",
	SCHEDULE_START: "Schedule start",
	SCHEDULE_END: "Schedule end",
	CURRENT_TRIP_DELIVERY: "After",
	PREVIOUS_TRIP_DELIVERY: "After",
	NEXT_TRIP_PICKUP: "Next",
	TRIP_PICKUP: "Trip pickup",
	TRIP_DELIVERY: "Trip delivery",
	PICKUP_ORIGIN_YARD: "Pickup origin yard",
	PICKUP_DEST_YARD: "Pickup destination yard",
	DELIVERY_ORIGIN_YARD: "Delivery origin yard",
	DELIVERY_DEST_YARD: "Delivery destination yard",
	RELAY_DESTINATION_YARD: "Relay destination yard",
	DRIVER_HOME: "Driver home",
	TRUCK_YARD: "Home yard",
};

// Render the precomputed search endpoint (origin or destination) the backend resolved for this
// (truck, day) row. Backend resolution order: trip-derived (previous delivery / next pickup) >
// yard fallback > none. The caption indicates which source was used so dispatchers know why we
// pointed search there.
const renderSearchEndpoint = (endpoint) => {
	const label = endpoint?.address || (endpoint?.city && endpoint?.state ? `${endpoint.city}, ${endpoint.state}` : null);
	if (!label) return <Typography variant="body2">N/A</Typography>;
	const sourceText = endpoint?.source ? ENDPOINT_SOURCE_LABELS[endpoint.source] || endpoint.source : null;
	const loadIdText = endpoint?.load_id ? ` (${endpoint.load_id})` : "";
	const caption = sourceText ? `${sourceText}${loadIdText}` : null;
	return (
		<Box>
			<Typography variant="body2">{label}</Typography>
			{caption && (
				<Typography variant="caption" style={{ color: "#757575", display: "block", lineHeight: 1.1 }}>
					{caption}
				</Typography>
			)}
		</Box>
	);
};

const exportSearchEndpoint = (endpoint) => {
	if (!endpoint) return "N/A";
	return endpoint.address || (endpoint.city && endpoint.state ? `${endpoint.city}, ${endpoint.state}` : "N/A");
};

// Render pickup/delivery schedule chips. Compared to the relay version this drops longhaul-only
// branches and the deferred placeholder paths since deferred relay chains free their trucks back
// into the live queue (so a deferred row never lands here).
const buildCoveredCaption = (trip) => {
	const truckIdLabel = trip?.truck?.truckid ? `#${trip.truck.truckid}` : "";
	const loadIdLabel = trip?.load_id ? trip.load_id : trip?.trip_id || trip?._id;
	return [truckIdLabel, loadIdLabel].filter(Boolean).join(" - ");
};

const tripIsRelayChild = (trip) => trip?.is_relay_trip === true && trip?.is_relay_parent !== true;

// Trip-driven rows: always render the row-day trips as chips (no schedule slots anymore).
const renderScheduleChips = (row, dispatch, carrierId) => {
	const chips = [];

	const openTripView = (trip) => {
		if (!dispatch) return;
		const tripId = trip.trip_id || trip._id;
		const tripCarrierId = trip.carrier || carrierId;
		if (!tripId) return;
		dispatch(openFormDialog({ viewId: "TRIP_VIEW", dataIds: { tripId, carrierId: tripCarrierId } }));
	};

	const tripChipSource = tripsActiveOnRowDay(row).length > 0 ? tripsActiveOnRowDay(row) : row.covering_trips || [];
	const orderedCoveringTrips = [...tripChipSource].sort((a, b) => {
		const av = moment(a?.pickup_date || a?.pickup_window_date || a?.delivery_date || 0).valueOf();
		const bv = moment(b?.pickup_date || b?.pickup_window_date || b?.delivery_date || 0).valueOf();
		return (Number.isFinite(av) ? av : 0) - (Number.isFinite(bv) ? bv : 0);
	});
	orderedCoveringTrips.forEach((trip, index) => {
		const id = trip?.trip_id || trip?._id;
		const isRelay = tripIsRelayChild(trip);
		const palette = isRelay ? { bg: "#f3e5f5", color: "#6a1b9a" } : { bg: "#e3f2fd", color: "#1565c0" };
		const tooltip = `${isRelay ? "Relay child" : "Live load"}: ${buildCoveredCaption(trip)}`;
		const ChipIcon = isRelay ? OnRelayIcon : CompletedIcon;
		const isClickable = dispatch && id;
		chips.push(
			<Tooltip key={`trip-${index}`} title={tooltip}>
				<Chip
					size="small"
					label={`# ${trip.load_id || id}`}
					style={{
						backgroundColor: palette.bg,
						color: palette.color,
						margin: "2px",
						fontSize: "0.75rem",
						cursor: isClickable ? "pointer" : "default",
					}}
					icon={<ChipIcon style={{ fontSize: "0.8rem" }} />}
					onClick={isClickable ? () => openTripView(trip) : undefined}
				/>
			</Tooltip>
		);
	});

	return chips;
};

// Default window aligned with backend `getLiveQueue`: yesterday … today + 3 days inclusive.
const LIVE_QUEUE_PAST_DAYS = 1;
const LIVE_QUEUE_FUTURE_DAYS = 3;

const liveQueueList = (
	role,
	dispatch,
	readOnly = false,
	isMainHandler = false,
	_dateRangeLegacy = LIVE_QUEUE_FUTURE_DAYS,
	history
) => {
	const isInternal = authRoles.internalUsers.includes(role);
	const isCarrier = authRoles.carrierPowerUsers.includes(role) || authRoles.carrierDispatcher.includes(role);
	const isDriver = authRoles.driver.includes(role);

	return {
		urlGET: "api/profile/carriers/:carrierId/relay-schedules/live-queue",
		idKey: "id",
		listenEvent: ["profileRevision", "tripsRevision"],
		segmentKey: "LIVE_QUEUE",
		content: {
			pagination: {
				// All local trucks × weekdays can exceed 100 rows; keep schedule chains on one page.
				defaut: { size: 500 },
				enabled: true,
				resetScroll: true,
			},
			table: {
				variant: "skinny",
				multiline: true,
				orderBy: ["search_pickup_date", "truck.truckid", "urgency", "total_schedules", "date"],
				getRowStyle: (item) => {
					const rowDate = item.date ? moment(item.date) : null;
					if (!rowDate || !rowDate.isValid()) return {};
					return rowDate.date() % 2 === 1 ? { backgroundColor: "rgba(0, 0, 0, 0.06)" } : {};
				},
			},
			order: {
				defaut: { key: "search_pickup_date", dir: "asc" },
			},
			defaults: {
				items: [
					{
						label: "All",
						filters: { status: "__NOT_SELECTED__", urgency: "__NOT_SELECTED__" },
						sort: { key: "search_pickup_date", dir: "asc" },
					},
					{
						label: "Available",
						filters: { status: "AVAILABLE", urgency: "__NOT_SELECTED__" },
						sort: { key: "search_pickup_date", dir: "asc" },
					},
					{
						label: "On Relay + Available",
						filters: { status: "ON_RELAY,AVAILABLE", urgency: "__NOT_SELECTED__" },
						sort: { key: "search_pickup_date", dir: "asc" },
					},
					{
						label: "On Relay + Completed",
						filters: { status: "ON_RELAY,COMPLETED", urgency: "__NOT_SELECTED__" },
						sort: { key: "search_pickup_date", dir: "asc" },
					},
					{
						label: "On Relay",
						filters: { status: "ON_RELAY", urgency: "__NOT_SELECTED__" },
						sort: { key: "search_pickup_date", dir: "asc" },
					},
					{
						label: "Completed",
						filters: { status: "COMPLETED", urgency: "__NOT_SELECTED__" },
						sort: { key: "search_pickup_date", dir: "asc" },
					},
					{
						label: "Deferred",
						filters: { status: "DEFERRED", urgency: "__NOT_SELECTED__" },
						sort: { key: "search_pickup_date", dir: "asc" },
					},
					{
						label: "Urgent",
						filters: { status: "__NOT_SELECTED__", urgency: "CRITICAL,HIGH" },
						sort: { key: "urgency", dir: "asc" },
					},
				],
			},
			filters: {
				search: true,
				searchDescription: "Search by Truck ID, Schedule name, Location, or Load ID",
				default: {
					chainRange: `${moment()
						.subtract(LIVE_QUEUE_PAST_DAYS, "days")
						.startOf("day")
						.valueOf()}__DATE_RANGE__${moment().add(LIVE_QUEUE_FUTURE_DAYS, "days").endOf("day").valueOf()}`,
				},
				items: [
					...(isCarrier && !isDriver && !isMainHandler
						? [
								{
									key: "truck",
									type: "autocomplete",
									label: "Truck",
									translate: "",
									field: {
										noErrorMessage: true,
										classes: { root: "makeStyles-field-1251" },
										variant: "outlined",
									},
									autocomplete: {
										provider: "smarthop",
										url: "api/profile/carriers/:carrierId/trucks",
										params: {
											options_metadata_truck: true,
											options_metadata_driver: true,
											options_active_only: true,
											label_format_show_driver: true,
										},
										preloadDefault: true,
										preloadImmedately: true,
										listenEvent: "profileRevision",
									},
								},
						  ]
						: []),
					{
						key: "chainRange",
						type: "dateRange",
						label: "Date Range",
						shortcutPriority: "long",
					},
					...(isInternal || isMainHandler
						? [
								{
									key: "carrier",
									type: "autocomplete",
									label: "Carrier",
									field: { noErrorMessage: true, classes: {} },
									autocomplete: {
										provider: "smarthop",
										url: "api/profile/carriers",
										preloadDefault: true,
										listenEvent: "profileRevision",
									},
								},
						  ]
						: []),
				],
			},
			items: [
				{
					key: "id",
					type: "id",
					label: "Row ID",
					hidden: true,
				},
				{
					key: "status",
					type: "component",
					builder: (item) => {
						const status = item.coverage_status || "AVAILABLE";
						const label = STATUS_LABELS[status] || status;
						// Trip-driven rows: status is purely gap-based. COMPLETED means the day is
						// booked with too little room for another load; AVAILABLE means there's a
						// searchable gap. The caption surfaces the day's booked load IDs either way.
						const loadIds = tripsActiveOnRowDay(item)
							.map((t) => t.load_id)
							.filter(Boolean);
						const loadIdsCaption = loadIds.length > 0 ? `# ${loadIds.join(", ")}` : null;
						let caption;
						if (status === "COMPLETED") {
							caption =
								item.coverage_reason_code === "INSUFFICIENT_GAP"
									? loadIdsCaption
										? `No gap to fill · ${loadIdsCaption}`
										: "No gap to fill"
									: loadIdsCaption || "Booked";
						} else {
							caption = loadIdsCaption || "Live-load day";
						}
						return (
							<Box display="flex" flexDirection="column">
								<Box display="flex" alignItems="center">
									{getStatusIcon(status)}
									<Typography variant="body2">{label}</Typography>
								</Box>
								{caption && (
									<Typography
										variant="caption"
										style={{ color: "#616161", fontSize: "0.65rem", lineHeight: 1.2, marginTop: 2 }}
									>
										{caption}
									</Typography>
								)}
							</Box>
						);
					},
					label: "Status",
					table: { width: 140, minWidth: 100 },
					export: {
						builder: (item) => item.coverage_status || "AVAILABLE",
					},
				},
				{
					key: "truck__view",
					type: "truck__view",
					label: "Truck",
					viewConfig: {
						readOnly,
						showPhone: false,
						showTeam: true,
					},
					table: { width: 160, minWidth: 100 },
				},
				{
					// Pickup Date = last delivery on the chain day (when the truck is free at origin),
					// else earliest schedule start on the row.
					key: "search_pickup_date",
					type: "component",
					builder: (item) =>
						renderSearchDate(item, resolvePickupDateMoment, PICKUP_DATE_SOURCE_LABELS, item.search_pickup_date_source),
					label: "Pickup Date",
					table: { width: 130, minWidth: 100 },
					export: {
						builder: (item) => exportSearchDate(item, resolvePickupDateMoment),
					},
				},
				{
					// Origin = last row-day delivery location ("After"), else schedule/yard fallback.
					key: "origin",
					type: "component",
					builder: (item) => renderSearchEndpoint(item.search_origin),
					label: "Origin",
					table: { width: 140, minWidth: 100 },
					export: { builder: (item) => exportSearchEndpoint(item.search_origin) },
				},
				{
					// Delivery Date = earliest pickup on the chain day after the anchor delivery,
					// else latest schedule end on the row.
					key: "search_delivery_date",
					type: "component",
					builder: (item) =>
						renderSearchDate(
							item,
							resolveDeliveryDateMoment,
							DELIVERY_DATE_SOURCE_LABELS,
							item.search_delivery_date_source
						),
					label: "Delivery Date",
					table: { width: 130, minWidth: 100 },
					export: {
						builder: (item) => exportSearchDate(item, resolveDeliveryDateMoment),
					},
				},
				{
					// Destination = next row-day pickup after anchor ("Next"), else schedule/yard fallback.
					key: "destination",
					type: "component",
					builder: (item) => renderSearchEndpoint(item.search_destination),
					label: "Destination",
					table: { width: 140, minWidth: 100 },
					export: { builder: (item) => exportSearchEndpoint(item.search_destination) },
				},
				{
					// Time between Pickup Date and Delivery Date (window to cover with a live load).
					key: "search_time_gap",
					type: "component",
					builder: (item) => <Typography variant="body2">{formatTimeGap(item)}</Typography>,
					label: "Time Gap",
					table: { width: 90, minWidth: 72 },
					export: {
						builder: (item) => formatTimeGap(item),
					},
				},
				{
					key: "schedule_chain",
					type: "component",
					builder: (item) => {
						const rowCarrierId =
							item.carrier || item.pickup_schedules?.[0]?.carrier_id || item.delivery_schedules?.[0]?.carrier_id;
						return (
							<Box flexWrap="wrap" alignItems="center">
								{renderScheduleChips(item, dispatch, rowCarrierId)}
							</Box>
						);
					},
					label: "Schedules",
					table: { width: 280, minWidth: 220 },
					export: {
						builder: (item) => {
							const types = [];
							if (item.pickup_schedules?.length > 0) types.push(`P:${item.pickup_schedules.length}`);
							if (item.delivery_schedules?.length > 0) types.push(`D:${item.delivery_schedules.length}`);
							return types.join(" | ");
						},
					},
				},
				{
					key: "covering_trips",
					type: "trips__view",
					label: "Active Trips",
					viewConfig: {
						readOnly,
						showBroker: true,
					},
					builder: (item) => {
						const activeTrips = tripsActiveOnRowDay(item);
						if (activeTrips.length > 0) {
							return activeTrips.map((trip) => ({
								label: `# ${trip.load_id}`,
								value: trip.trip_id,
								metadata: {
									carrier: trip?.carrier || "",
									load_id: trip.load_id,
									broker: trip?.broker || "",
									pickup_address: trip?.pickup_address,
									delivery_address: trip?.delivery_address,
									status: trip?.status,
									relay_type: trip?.relay_type,
								},
							}));
						}
						return [];
					},
					export: {
						builder: (item) => {
							const activeTrips = tripsActiveOnRowDay(item);
							if (!activeTrips.length) return "None";
							return activeTrips.map((t) => `# ${t.load_id}`).join(", ");
						},
					},
					table: { width: 100, minWidth: 80 },
				},
				{
					key: "menu",
					type: "menu",
					label: "Actions",
					table: { width: 80, sticky: true },
					items: [
						{
							key: "search_loads",
							label: "Search for Loads",
							icon: "search",
							dispatchAction: (item, dataIds, dispatch, history) => {
								handleSearchForLoads(item, history);
							},
						},
						{
							variant: "divider",
						},
						{
							key: "start_agent",
							label: "Start Agent",
							icon: "play_arrow",
							disabled: (item) => {
								const status = item.coverage_status || "AVAILABLE";
								return status === "COMPLETED" || status === "DEFERRED" || !item.truck?._id;
							},
							dispatchAction: async (item, dataIds, dispatch, history, snackbar) => {
								await handleStartAgent(item, dispatch, snackbar);
							},
						},
						{
							key: "update_truck_plan",
							label: "Update Truck Plan Only",
							icon: "local_shipping",
							disabled: (item) => {
								const status = item.coverage_status || "AVAILABLE";
								return status === "COMPLETED" || status === "DEFERRED" || !item.truck?._id;
							},
							dialogBuilder: (item) => {
								const truckLabel = item.truck__view?.label || `Truck #${item.truck?.truckid}` || "Unknown";
								const origin = searchEndpointLabel(item.search_origin) || "N/A";
								const destination = searchEndpointLabel(item.search_destination) || "N/A";
								const carrierId = item.carrier || item.carrier_id;
								return {
									viewId: "CONFIRM_DIALOG",
									mode: "CREATE",
									dataIds: {
										title: "Update Truck Plan",
										message: `This will update the truck plan from this live queue row.\n\nTruck: ${truckLabel}\nDay: ${item.date}\nOrigin: ${origin}\nDestination: ${destination}\n\nThe truck plan will be configured for automatic load booking (live-load settings).`,
										confirmLabel: "Update Plan",
										url: `api/profile/carriers/${carrierId}/relay-schedules/chains/update-truck-plan`,
										method: "POST",
										body: { data: item },
									},
								};
							},
						},
					],
				},
				{
					key: "total_schedules",
					type: "text",
					label: "Total Schedules",
					hidden: true,
					builder: (item) => item.total_schedules,
					export: { builder: (item) => item.total_schedules },
				},
			],
			export: {
				fileName: "Live Queue",
			},
		},
	};
};

export default liveQueueList;
